import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDaysIcon, MapPinIcon, WrenchScrewdriverIcon } from '@heroicons/react/20/solid';
import type { OwnerCard } from '../../types/buildExpansion';

/**
 * Optional owner identity card on the build page. Every field is
 * owner-opted-in. Location is always the broad value provided by the
 * backend; never render anything more precise.
 */

/** Whole years owned, from an ownership_start date string. */
const yearsOwnedLabel = (ownershipStart: string | null | undefined): string | null => {
  if (!ownershipStart) return null;
  const start = new Date(ownershipStart);
  if (Number.isNaN(start.getTime())) return null;
  const ms = Date.now() - start.getTime();
  if (ms < 0) return null;
  const years = Math.floor(ms / (365.25 * 24 * 60 * 60 * 1000));
  if (years < 1) return 'Owned less than a year';
  return `Owned ${years} ${years === 1 ? 'year' : 'years'}`;
};

const asText = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

export interface OwnerIdentityCardProps {
  card: OwnerCard | null | undefined;
  username: string;
  avatarUrl?: string | null;
  /** Mirrors BuildProfile.owner_card_enabled; renders nothing when false. */
  enabled?: boolean;
  className?: string;
}

export const OwnerIdentityCard: React.FC<OwnerIdentityCardProps> = ({
  card,
  username,
  avatarUrl,
  enabled = true,
  className = '',
}) => {
  if (!enabled || !card) return null;

  const displayName = asText(card.display_name);
  const bio = asText(card.bio);
  const location = asText(card.location);
  const role = asText(card.role);
  const yearsOwned = yearsOwnedLabel(
    asText(card.ownership_start) || asText(card.ownership_since)
  );

  if (!displayName && !bio && !location && !role && !yearsOwned) return null;

  const metaItems = [
    role ? { key: 'role', Icon: WrenchScrewdriverIcon, text: role } : null,
    location ? { key: 'location', Icon: MapPinIcon, text: location } : null,
    yearsOwned ? { key: 'years', Icon: CalendarDaysIcon, text: yearsOwned } : null,
  ].filter((item): item is { key: string; Icon: typeof MapPinIcon; text: string } => item != null);

  return (
    <section className={`card p-5 sm:p-6 ${className}`} aria-label="About the owner">
      <div className="flex items-start gap-3.5">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="w-12 h-12 rounded-full object-cover ring-1 ring-white/[0.08] shrink-0"
          />
        ) : (
          <span className="w-12 h-12 rounded-full bg-[#111] flex items-center justify-center text-base font-semibold text-gray-300 ring-1 ring-white/[0.08] shrink-0">
            {(displayName || username).charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-meta">Owner</p>
          <Link
            to={`/${username}`}
            className="text-sm font-medium text-white hover:text-orange-200 transition-colors duration-200 truncate block mt-0.5"
          >
            {displayName || `@${username}`}
          </Link>
          {displayName && <p className="text-meta">@{username}</p>}
        </div>
      </div>

      {bio && <p className="text-sm text-gray-400 leading-relaxed mt-3">{bio}</p>}

      {metaItems.length > 0 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 list-none">
          {metaItems.map(({ key, Icon, text }) => (
            <li key={key} className="inline-flex items-center gap-1.5 text-xs text-gray-400">
              <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" aria-hidden />
              {text}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default OwnerIdentityCard;
